var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { TextField } from "@mui/material";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
export const IntegerNumberFormat = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: false,
});
export function NumberField(_a) {
    var { numberValue, onChangeNumber, onChange, onFocus, onBlur, onKeyPress, numberFormat, invalidMessage, helperText } = _a, props = __rest(_a, ["numberValue", "onChangeNumber", "onChange", "onFocus", "onBlur", "onKeyPress", "numberFormat", "invalidMessage", "helperText"]);
    const [strValue, setStrValue] = useState("");
    const [error, setError] = useState("");
    const [focus, setFocus] = useState(false);
    const input = useRef(null);
    // find out decimal separator
    const decimalSeparator = useMemo(() => {
        return numberFormat.format(1.1).replace(/[01]/g, '');
    }, [numberFormat]);
    // find out thousands separator
    const thousandSeparator = useMemo(() => {
        return numberFormat.format(1111).replace(/[01]/g, '').replace(decimalSeparator, '');
    }, [numberFormat, decimalSeparator]);
    // restrict key press to chars that can be part of a number
    const handleKeyPress = useCallback((e) => {
        const keyValue = String.fromCharCode(e.charCode);
        if (!keyValue.match(/[\-0-9]/g) && keyValue !== thousandSeparator && keyValue !== decimalSeparator) {
            e.preventDefault();
        }
        else {
            onKeyPress && onKeyPress(e);
        }
    }, [onKeyPress, thousandSeparator, decimalSeparator]);
    // set controlled string value and call onChange with numeric value
    const handleChange = useCallback((e) => {
        const { value } = e.currentTarget;
        setStrValue(value);
        onChangeNumber(value ? parseNumber(value, thousandSeparator, decimalSeparator) : null);
        onChange && onChange(e);
    }, [thousandSeparator, decimalSeparator, onChange]);
    // remember focus
    const handleFocus = useCallback(e => {
        setFocus(true);
        onFocus && onFocus(e);
    }, [onFocus]);
    // format on blur
    const handleBlur = useCallback((e) => {
        setFocus(false);
        onBlur && onBlur(e);
    }, [onBlur, numberFormat, numberValue]);
    // format when number value changes and user is not editing
    useEffect(() => {
        if (!focus) {
            if (numberValue !== null && Number.isFinite(numberValue)) {
                setStrValue(numberFormat.format(numberValue));
            }
        }
    }, [focus, numberValue]);
    // set error message when user entered a string that is NaN
    useEffect(() => {
        const valid = (numberValue === null || Number.isFinite(numberValue));
        setError(valid ? "" : (invalidMessage || "Invalid number"));
    }, [numberValue, invalidMessage]);
    // apply error to HTML5 form validation
    useEffect(() => {
        if (input.current) {
            input.current.setCustomValidity(error);
        }
    }, [error]);
    return (React.createElement(TextField, Object.assign({}, props, { value: strValue, inputRef: input, error: !!error, helperText: error || helperText, onFocus: handleFocus, onBlur: handleBlur, onKeyPress: handleKeyPress, onChange: handleChange })));
}
function parseNumber(value, thousandSeparator, decimalSeparator) {
    if (thousandSeparator) {
        value = replaceAll(value, thousandSeparator, "");
    }
    if (decimalSeparator) {
        value = replaceAll(value, decimalSeparator, ".");
    }
    return Number.parseFloat(value);
}
function replaceAll(str, search, replacement) {
    return str.split(search).join(replacement);
}
//# sourceMappingURL=NumberField.js.map